import { projectSchema, type ProjectStage } from '@si/types';
import { get, objectToQueryParams } from '@si/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { queryKeys } from './queryKeys';
import { useUser } from './useUser';

const emptyObject = {} as const;
const responseSchema = z.object({
  projects: z.array(projectSchema),
});

interface Params {
  includeCompany?: boolean;
  includeTeam?: boolean;
  includeGatewayStatus?: boolean;
  includeSensorStatus?: boolean;
  stages?: ProjectStage[];
}

export function useProjects({
  includeCompany = false,
  includeTeam = false,
  includeGatewayStatus = false,
  includeSensorStatus = false,
  stages,
}: Params = emptyObject) {
  const { user } = useUser();
  const url = `/projects${objectToQueryParams({
    includeCompany,
    includeTeam,
    includeGatewayStatus,
    includeSensorStatus,
    stages,
  })}`;

  const { data, error, isLoading, isRefetching, refetch } = useQuery({
    queryKey: queryKeys.getProjects(
      includeCompany,
      includeTeam,
      includeGatewayStatus,
      includeSensorStatus,
      stages
    ),
    queryFn: () => get({ url, schema: responseSchema }),
    placeholderData: keepPreviousData,
  });

  return {
    projects: (data?.projects || []).filter(
      (project) =>
        user?.isSiAdmin ||
        (!project.isUserHidden && (project.projectStage !== 'archived' || user?.isDistributorUser))
    ),
    error,
    isLoading,
    isRefetching,
    refetch,
  };
}
